import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { addMember, listMembers, removeMember, updateMemberRole } from '@/api/tenants';
import { queryKeys } from '@/lib/query-keys';
import type { Role } from '@/types/domain';

/**
 * Equipo del negocio activo (módulo 1, rutas `.../users`).
 *
 * OJO: el backend protege TODAS estas rutas con `requireRole('ADMIN')`, incluido el `GET`
 * (`backend/src/modules/tenants/tenants.routes.ts`). Un AGENTE_HUMANO recibe 403 incluso al
 * listar, así que la pestaña de Equipo se muestra solo a administradores.
 *
 * Reglas de negocio que el backend valida (y que la pantalla no duplica, solo muestra el
 * mensaje): no se puede degradar ni eliminar al último ADMIN del tenant, el email debe
 * corresponder a un usuario ya registrado (404) y no se puede agregar dos veces al mismo
 * usuario (409).
 */

/** `GET /api/tenants/:tenantId/users` (solo ADMIN). */
export function useMembers(tenantId: string | null) {
  return useQuery({
    queryKey: queryKeys.tenants.members(tenantId ?? ''),
    queryFn: () => listMembers(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

function invalidateMembers(queryClient: QueryClient, tenantId: string | null): void {
  if (tenantId) queryClient.invalidateQueries({ queryKey: queryKeys.tenants.members(tenantId) });
}

/** `POST /api/tenants/:tenantId/users` (solo ADMIN). */
export function useAddMember(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: Role }) =>
      addMember(tenantId ?? '', { email, role }),
    onSuccess: () => invalidateMembers(queryClient, tenantId),
  });
}

/** `PATCH /api/tenants/:tenantId/users/:userId` (solo ADMIN). */
export function useUpdateMemberRole(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      updateMemberRole(tenantId ?? '', userId, { role }),
    onSuccess: () => invalidateMembers(queryClient, tenantId),
  });
}

/** `DELETE /api/tenants/:tenantId/users/:userId` (solo ADMIN). */
export function useRemoveMember(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => removeMember(tenantId ?? '', userId),
    onSuccess: () => invalidateMembers(queryClient, tenantId),
  });
}
